import {
  ApolloClient,
  InMemoryCache,
  from,
  type NormalizedCacheObject,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { getWorkspaceHeader, registerWorkspaceScopedClient } from "@ticketon/marketing/workspace";
import { sessionManager } from "@/auth/sessionBridge";
import { buildLocaleHeader } from "@/i18n/localeHeader";

const TRAVEL_ADMIN_GRAPHQL_URI = "/platform/admin/travel/graphql";

let travelAdminClient: ApolloClient<NormalizedCacheObject> | null = null;

function getAuthHeader(): Record<string, string> {
  const session = sessionManager.getSession();
  if (session?.token) {
    return { Authorization: `Bearer ${session.token}` };
  }
  return {};
}

export function getTravelAdminClient(): ApolloClient<NormalizedCacheObject> {
  if (!travelAdminClient) {
    // Upload link sends File variables as multipart/form-data (GraphQL
    // multipart request spec) instead of JSON, so `Upload!` mutations work.
    const httpLink = createUploadLink({
      uri: `${import.meta.env.VITE_API_ENDPOINT.endsWith('/')
        ? import.meta.env.VITE_API_ENDPOINT.slice(0, -1)
        : import.meta.env.VITE_API_ENDPOINT}${TRAVEL_ADMIN_GRAPHQL_URI}`,
    });

    const authLink = setContext((_, { headers }) => {
      const authHeaders = getAuthHeader();
      return {
        headers: {
          ...(headers || {}),
          ...buildLocaleHeader(),
          ...authHeaders,
          // myPermissions also returns the caller's Marketing: grants, which depend on the
          // active marketing workspace, so travel requests carry its id too.
          ...getWorkspaceHeader(),
          // Required by HotChocolate's CSRF protection for multipart (file upload) requests.
          "GraphQL-preflight": "1",
        },
      };
    });

    travelAdminClient = new ApolloClient({
      link: from([authLink, httpLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "cache-and-network",
        },
      },
    });

    // Permissions are re-read when the workspace changes (it is selected once, at start-up).
    registerWorkspaceScopedClient(travelAdminClient);
  }
  return travelAdminClient;
}

export function resetTravelAdminClient(): void {
  travelAdminClient = null;
}
